/**
 * URL search-param contract for the Action Items worklist.
 *
 * The page's search term, filters, sort and pagination live in the URL, the
 * same way `Employees.page.tsx` does it, so a deep link, a refresh and the
 * back button all reproduce the list the user was looking at — and so a
 * filter change no longer resets the rest of the state.
 *
 * `scope` carries the one piece of state that is not a filter: whose work is
 * being shown. It defaults to `mine`, which is what makes "Assigned to me"
 * the landing view on every nav-in without pinning it — `scope=all` widens
 * the list to every accessible case, and being in the URL it survives a
 * refresh like everything else.
 *
 * @module pages/dashboard/action-items/search
 */

import type { SortState } from "@mitratech/north-star";

import {
  toSearchEnum,
  toSearchInt,
  toSearchPageSize,
  toSearchString,
  VALID_PAGE_SIZES,
} from "../../../lib/searchParams";

import type { ActionItemsView } from "./types";

/** Whose action items the list shows. */
export const ACTION_ITEM_SCOPES = ["mine", "all"] as const;
export type ActionItemScope = (typeof ACTION_ITEM_SCOPES)[number];

/**
 * The sortable columns — deliberately four.
 *
 * These are the DataTable column keys *and* the API's `ordering` keys minus
 * the `employee_name` alias below, so a header click needs no lookup table
 * beyond {@link toOrderingParam}. Priority, case, task and source are not
 * sortable: the spec asks for these four.
 */
export const ACTION_ITEM_SORT_KEYS = [
  "employee_name",
  "assigned_to",
  "status",
  "due_date",
] as const;
export type ActionItemSortKey = (typeof ACTION_ITEM_SORT_KEYS)[number];

/** Column keys whose API `ordering` key differs from the column key. */
const ORDERING_ALIASES: Partial<Record<ActionItemSortKey, string>> = {
  employee_name: "employee",
};

/** URL search parameters for the Action Items worklist. */
export interface ActionItemsSearch {
  /** `mine` (default) or `all` accessible cases. */
  scope: ActionItemScope;
  /** Free-text search over employee name, case id and task title. */
  filterTerm: string;
  status: string;
  priority: string;
  /** Person or role the item is assigned to; see `toAssigneeFilterParams`. */
  assigned_to: string;
  /** Employee id — a real filter, distinct from the free-text search. */
  employee: string;
  /** Relative due-date bucket ("overdue" | "week" | "month" | "none"). */
  due: string;
  /** Sorted column, or "" for the server's default order. */
  sort: ActionItemSortKey | "";
  /** Sort direction; only meaningful with `sort`. */
  dir: "asc" | "desc";
  /** 1-based page number. */
  page: number;
  pageSize: (typeof VALID_PAGE_SIZES)[number];
}

/**
 * Normalizes raw URL params into {@link ActionItemsSearch}.
 *
 * Every value is clamped to what the UI can represent, so a hand-edited or
 * stale link degrades to a sane list instead of forwarding junk to the API or
 * leaving a `<select>` on an unmatched value.
 */
export function parseActionItemsSearch(
  search: Record<string, unknown>,
): ActionItemsSearch {
  const scope = toSearchEnum(search.scope, ACTION_ITEM_SCOPES);
  const sort = toSearchEnum(search.sort, ACTION_ITEM_SORT_KEYS);

  return {
    scope: scope === "" ? "mine" : scope,
    filterTerm: toSearchString(search.filterTerm),
    status: toSearchEnum(search.status, [
      "to_do",
      "in_progress",
      "complete",
      "archived",
    ] as const),
    priority: toSearchEnum(search.priority, ["low", "medium", "high"] as const),
    // Free-form: a user id, an employee id, "unassigned", or a "type:" value.
    assigned_to: toSearchString(search.assigned_to),
    employee: toSearchString(search.employee),
    due: toSearchEnum(search.due, [
      "overdue",
      "week",
      "month",
      "none",
    ] as const),
    sort,
    // Descending is only reachable with a sorted column, so the default is
    // harmless when `sort` is "".
    dir: toSearchEnum(search.dir, ["asc", "desc"] as const) === "desc"
      ? "desc"
      : "asc",
    page: toSearchInt(search.page, 1),
    pageSize: toSearchPageSize(search.pageSize),
  };
}

/**
 * Seeds search params from a legacy `/action-items/$view` path segment.
 *
 * The sidebar's four entries used to be separate routes that reset their
 * filters on mount. They are now the same page with different starting
 * params, so those URLs stay linkable.
 */
export function searchForView(
  view: string,
  search: Record<string, unknown>,
): ActionItemsSearch {
  const parsed = parseActionItemsSearch(search);
  const viewDefaults: Record<ActionItemsView, Partial<ActionItemsSearch>> = {
    all: { scope: "all" },
    my: { scope: "mine" },
    unassigned: { scope: "all", assigned_to: "unassigned" },
    overdue: { scope: "all", due: "overdue" },
  };
  const defaults = viewDefaults[view as ActionItemsView];

  if (defaults === undefined) {
    return parsed;
  }

  // Seed only what the URL does not already say, so a filter the user changed
  // while sitting on one of these paths is not reverted on the next render.
  const seeded = { ...parsed };

  for (const [key, value] of Object.entries(defaults)) {
    if (search[key] === undefined) {
      Object.assign(seeded, { [key]: value });
    }
  }

  return seeded;
}

/** The API `ordering` value for the current sort, or undefined when unsorted. */
export function toOrderingParam(
  sort: ActionItemSortKey | "",
  dir: "asc" | "desc",
): string | undefined {
  if (sort === "") {
    return undefined;
  }

  const field = ORDERING_ALIASES[sort] ?? sort;

  return dir === "desc" ? `-${field}` : field;
}

/** The DataTable sort state for the current search, or null when unsorted. */
export function toSortState(
  sort: ActionItemSortKey | "",
  dir: "asc" | "desc",
): SortState | null {
  if (sort === "") {
    return null;
  }

  return { columnKey: sort, direction: dir };
}

/** The search params a DataTable sort change maps to. */
export function fromSortState(
  next: SortState | null,
): Pick<ActionItemsSearch, "sort" | "dir"> {
  if (next === null) {
    return { sort: "", dir: "asc" };
  }

  const sort = toSearchEnum(next.columnKey, ACTION_ITEM_SORT_KEYS);

  return { sort, dir: next.direction === "desc" ? "desc" : "asc" };
}
